import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Briefcase, Calendar, User, MessageCircle,
  Search, Users, Heart, Star, ClipboardCheck, Building2,
  HelpCircle, Award, GripVertical, Settings, Eye, EyeOff,
  ChevronUp, ChevronDown, FileText, AlertTriangle, BarChart3,
  Upload, DollarSign, Sparkles, Palette, Camera, Zap, Inbox, Shield, Megaphone, Bell, Video, Trash2, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * QuickActionBar — Vertical stacked navigation with drag-and-drop reordering.
 * 
 * RULES:
 * - Navigation/workflow actions ONLY
 * - NO posting tools, captions, hashtags, or social features
 */

// Fire-and-forget usage tracking
const trackQuickAction = (actionId, role, eventType = 'click') => {
  try {
    api.post('/analytics/quick-actions', { action_id: actionId, role, event_type: eventType }).catch(() => {});
  } catch {}
};

// Color-coded icon + pill bg + button styles
// Iter-221 — Community Feed promoted from the bottom of each role's list
// to a discoverable position near the top so it gets exercised before
// users scroll past it. Existing users who already customized their
// order via drag-and-drop keep their saved order (localStorage).
const TALENT_ACTIONS = [
  // Iter — Top 3 pinned for clarity: talent open Home and immediately see
  // Help (Evie), Payments (Settlements), and their Jobs (My Bookings).
  { id: 'support', label: 'Chat with Evie', icon: MessageCircle, path: '/talent/support', iconColor: 'text-orange-600', iconBg: 'bg-orange-100', btnStyle: 'border-orange-200 hover:bg-orange-50', testId: 'support-nav' },
  { id: 'settlements', label: 'Settlements', icon: DollarSign, path: '/talent/settlements', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'settlements-nav' },
  { id: 'bookings', label: 'My Bookings', icon: Calendar, path: '/talent/bookings', iconColor: 'text-violet-600', iconBg: 'bg-violet-100', btnStyle: 'border-violet-200 hover:bg-violet-50', testId: 'my-bookings-nav' },
  // Clock In lives in the contextual ClockInReminder banner on Home (one
  // obvious place) — intentionally NOT duplicated here to avoid confusion.
  { id: 'qr_scan', label: 'Scan Event QR', icon: Camera, path: '/talent/qr-scan', iconColor: 'text-cyan-600', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'qr-scan-nav' },
  { id: 'my_card', label: 'My Digital Card', icon: QrCode, path: '/talent/my-card', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'my-card-nav' },
  { id: 'browse', label: 'Browse Gigs', icon: Briefcase, path: '/talent/shifts', iconColor: 'text-teal-600', iconBg: 'bg-teal-100', btnStyle: 'border-teal-200 hover:bg-teal-50', testId: 'browse-gigs-nav' },
  { id: 'community', label: 'Community Feed', icon: Sparkles, path: '/community', iconColor: 'text-pink-600', iconBg: 'bg-pink-100', btnStyle: 'border-pink-200 hover:bg-pink-50', testId: 'community-feed-nav' },
  { id: 'direct_hire', label: 'Direct Booking Jobs', icon: Building2, path: '/talent/agency-jobs', iconColor: 'text-amber-600', iconBg: 'bg-amber-100', btnStyle: 'border-amber-200 hover:bg-amber-50', testId: 'direct-hire-nav' },
  { id: 'invitations', label: 'My Invitations', icon: Inbox, path: '/talent/invitations', iconColor: 'text-orange-600', iconBg: 'bg-orange-100', btnStyle: 'border-orange-200 hover:bg-orange-50', testId: 'my-invitations-nav' },
  { id: 'availability', label: 'My Availability', icon: Calendar, path: '/talent/availability', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'my-availability-nav' },
  { id: 'my_applications', label: 'Applied Gigs', icon: Inbox, path: '/talent/my-applications', iconColor: 'text-sky-600', iconBg: 'bg-sky-100', btnStyle: 'border-sky-200 hover:bg-sky-50', testId: 'applied-gigs-nav' },
  { id: 'portfolio', label: 'Portfolio', icon: Camera, path: '/talent/portfolio', iconColor: 'text-fuchsia-600', iconBg: 'bg-fuchsia-100', btnStyle: 'border-fuchsia-200 hover:bg-fuchsia-50', testId: 'portfolio-nav' },
  { id: 'messages', label: 'Messages', icon: MessageCircle, path: '/talent/messages-center', iconColor: 'text-blue-600', iconBg: 'bg-blue-100', btnStyle: 'border-blue-200 hover:bg-blue-50', testId: 'messages-nav' },
  { id: 'channels', label: 'Event Channels', icon: MessageCircle, path: '/talent/messages-center?tab=channels', iconColor: 'text-cyan-600', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'channels-nav' },
  { id: 'instant_payout', label: 'Instant Payout', icon: Zap, path: '/talent/instant-payout', iconColor: 'text-green-600', iconBg: 'bg-green-100', btnStyle: 'border-green-200 hover:bg-green-50', testId: 'instant-payout-nav' },
  { id: 'certifications', label: 'Certifications', icon: Award, path: '/talent/certifications', iconColor: 'text-purple-600', iconBg: 'bg-purple-100', btnStyle: 'border-purple-200 hover:bg-purple-50', testId: 'certifications-nav' },
  { id: 'rate', label: 'Rate Gigs', icon: Star, path: '/talent/ratings', iconColor: 'text-amber-500', iconBg: 'bg-amber-100', btnStyle: 'border-amber-200 hover:bg-amber-50', testId: 'rate-gigs-nav' },
  { id: 'quizzes', label: 'Assessments', icon: ClipboardCheck, path: '/talent/quizzes', iconColor: 'text-cyan-600', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'assessments-nav' },
  { id: 'profile_info', label: 'Profile', icon: User, path: '/talent/profile-info', iconColor: 'text-teal-600', iconBg: 'bg-teal-100', btnStyle: 'border-teal-200 hover:bg-teal-50', testId: 'profile-info-nav' },
  { id: 'scorecard', label: 'My Scorecard', icon: Award, path: '/talent/scorecard', iconColor: 'text-purple-600', iconBg: 'bg-purple-100', btnStyle: 'border-purple-200 hover:bg-purple-50', testId: 'scorecard-nav' },
  { id: 'referrals', label: 'Refer & Earn', icon: Heart, path: '/talent/referrals', iconColor: 'text-rose-500', iconBg: 'bg-rose-100', btnStyle: 'border-rose-200 hover:bg-rose-50', testId: 'referrals-nav' },
  { id: 'helpdesk', label: 'Help Desk', icon: HelpCircle, path: '/helpdesk', iconColor: 'text-indigo-500', iconBg: 'bg-indigo-100', btnStyle: 'border-indigo-200 hover:bg-indigo-50', testId: 'helpdesk-nav' },
];

const CLIENT_ACTIONS = [
  // Iter-184 — admin "View as Client" quick-edit shortcuts. These all land
  // on the existing /client/profile page with a hash anchor so the section
  // auto-scrolls into view.
  { id: 'edit_profile', label: 'Edit Profile', icon: User, path: '/client/profile#contact', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'edit-profile-nav' },
  { id: 'discover', label: 'Discover Talent', icon: Search, path: '/client/discover', iconColor: 'text-indigo-600', iconBg: 'bg-indigo-100', btnStyle: 'border-indigo-200 hover:bg-indigo-50', testId: 'discover-talent-nav' },
  { id: 'community', label: 'Community Feed', icon: Sparkles, path: '/community', iconColor: 'text-pink-600', iconBg: 'bg-pink-100', btnStyle: 'border-pink-200 hover:bg-pink-50', testId: 'community-feed-nav' },
  { id: 'manage_gigs', label: 'Manage Gigs', icon: Briefcase, path: '/client/shifts', iconColor: 'text-teal-600', iconBg: 'bg-teal-100', btnStyle: 'border-teal-200 hover:bg-teal-50', testId: 'manage-gigs-nav' },
  { id: 'company_info', label: 'Company Info', icon: Building2, path: '/client/profile#company', iconColor: 'text-indigo-700', iconBg: 'bg-indigo-100', btnStyle: 'border-indigo-200 hover:bg-indigo-50', testId: 'company-info-nav' },
  { id: 'about_client', label: 'About Client', icon: FileText, path: '/client/profile#about', iconColor: 'text-violet-700', iconBg: 'bg-violet-100', btnStyle: 'border-violet-200 hover:bg-violet-50', testId: 'about-client-nav' },
  { id: 'branding', label: 'Branding / Logo', icon: Palette, path: '/client/profile#logo', iconColor: 'text-fuchsia-700', iconBg: 'bg-fuchsia-100', btnStyle: 'border-fuchsia-200 hover:bg-fuchsia-50', testId: 'branding-nav' },
  { id: 'influencers', label: 'Creator Network', icon: Users, path: '/client/creators', iconColor: 'text-violet-600', iconBg: 'bg-violet-100', btnStyle: 'border-violet-200 hover:bg-violet-50', testId: 'influencer-discovery-btn' },
  { id: 'calendar', label: 'My Calendar', icon: Calendar, path: '/client/calendar', iconColor: 'text-cyan-600', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'client-calendar-nav' },
  { id: 'teams', label: 'My Teams', icon: Users, path: '/client/teams', iconColor: 'text-violet-600', iconBg: 'bg-violet-100', btnStyle: 'border-violet-200 hover:bg-violet-50', testId: 'my-teams-nav' },
  { id: 'my_plan', label: 'My Plan', icon: Sparkles, path: '/client/settings/subscription', iconColor: 'text-emerald-600', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'my-plan-nav' },
  { id: 'billing', label: 'Billing / Invoices', icon: DollarSign, path: '/client/billing', iconColor: 'text-emerald-700', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'client-billing-nav' },
  { id: 'manage_payments', label: 'Manage Payments', icon: Settings, path: '/client/billing?portal=open', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'manage-payments-nav' },
  { id: 'company_users', label: 'Company Users', icon: Users, path: '/client/company', iconColor: 'text-indigo-600', iconBg: 'bg-indigo-100', btnStyle: 'border-indigo-200 hover:bg-indigo-50', testId: 'company-users-nav' },
  { id: 'invoices', label: 'Invoices', icon: FileText, path: '/client/invoices', iconColor: 'text-orange-600', iconBg: 'bg-orange-100', btnStyle: 'border-orange-200 hover:bg-orange-50', testId: 'invoices-nav' },
  { id: 'messages', label: 'Messages', icon: MessageCircle, path: '/client/messages', iconColor: 'text-blue-600', iconBg: 'bg-blue-100', btnStyle: 'border-blue-200 hover:bg-blue-50', testId: 'messages-nav' },
  { id: 'referrals', label: 'Refer & Save $100', icon: Heart, path: '/client/referrals', iconColor: 'text-rose-500', iconBg: 'bg-rose-100', btnStyle: 'border-rose-200 hover:bg-rose-50', testId: 'referrals-nav' },
  { id: 'helpdesk', label: 'Help Center', icon: HelpCircle, path: '/helpdesk', iconColor: 'text-indigo-500', iconBg: 'bg-indigo-100', btnStyle: 'border-indigo-200 hover:bg-indigo-50', testId: 'client-helpdesk-nav' },
];

// Admin Quick Actions — permission keys align with admin_permissions from /api/home
// Iter-221 — Community Feed promoted near the top so admins see engagement
// metrics earlier in their session.
const ADMIN_ACTIONS = [
  { id: 'manage_users', label: 'Manage Users', icon: Users, path: '/admin/users', iconColor: 'text-gray-700', iconBg: 'bg-gray-100', btnStyle: 'border-gray-200 hover:bg-gray-50', testId: 'manage-users-nav' },
  { id: 'operations', label: 'Operations Center', icon: AlertTriangle, path: '/admin/operations', iconColor: 'text-amber-700', iconBg: 'bg-amber-100', btnStyle: 'border-amber-200 hover:bg-amber-50', testId: 'operations-nav-btn' },
  // Iter-246c — Daily operational shortcuts. Mel: ID Verification, GGF Audit,
  // and Recap Compliance are operational workflows, not platform settings.
  // Surfaced in the admin nav for single-click access.
  { id: 'idv_review', label: 'ID Verification Review', icon: ClipboardCheck, path: '/admin/idv-review', iconColor: 'text-amber-700', iconBg: 'bg-amber-100', btnStyle: 'border-amber-200 hover:bg-amber-50', testId: 'idv-review-nav' },
  { id: 'ggf_audit', label: 'GGF Certification Audit', icon: Shield, path: '/admin/ggf/audit', iconColor: 'text-emerald-700', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'ggf-audit-nav' },
  { id: 'recap_compliance', label: 'Recap Compliance', icon: MessageCircle, path: '/admin/recaps', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'recap-compliance-nav' },
  { id: 'community', label: 'Community Feed', icon: Sparkles, path: '/community', iconColor: 'text-pink-600', iconBg: 'bg-pink-100', btnStyle: 'border-pink-200 hover:bg-pink-50', testId: 'community-feed-nav' },
  { id: 'calendar', label: 'Operations Calendar', icon: Calendar, path: '/admin/calendar', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'admin-calendar-nav' },
  { id: 'helpdesk', label: 'Help Desk', icon: HelpCircle, path: '/admin/helpdesk', iconColor: 'text-violet-600', iconBg: 'bg-violet-100', btnStyle: 'border-violet-200 hover:bg-violet-50', testId: 'helpdesk-admin-nav' },
  { id: 'ratings', label: 'View All Ratings', icon: BarChart3, path: '/admin/ratings/all', iconColor: 'text-gray-700', iconBg: 'bg-gray-100', btnStyle: 'border-gray-200 hover:bg-gray-50', testId: 'ratings-nav' },
  { id: 'manage_quizzes', label: 'Manage Quizzes', icon: ClipboardCheck, path: '/quizzes/manage', iconColor: 'text-cyan-600', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'manage-quizzes-nav', requires: 'can_manage_talent_or_admins' },
  { id: 'csv_import', label: 'CSV Import', icon: Upload, path: '/admin/csv-import', iconColor: 'text-emerald-700', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'csv-import-nav', requires: 'can_import_csv' },
  { id: 'analytics', label: 'Financial Analytics', icon: DollarSign, path: '/admin/analytics', iconColor: 'text-green-700', iconBg: 'bg-green-100', btnStyle: 'border-green-200 hover:bg-green-50', testId: 'analytics-nav-btn', requires: 'can_view_financial' },
  { id: 'revenue', label: 'Revenue Dashboard', icon: DollarSign, path: '/admin/revenue', iconColor: 'text-emerald-700', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'revenue-nav-btn', requires: 'can_view_financial' },
  { id: 'admin_invoices', label: 'Invoices', icon: FileText, path: '/admin/invoices', iconColor: 'text-orange-700', iconBg: 'bg-orange-100', btnStyle: 'border-orange-200 hover:bg-orange-50', testId: 'admin-invoices-nav' },
  { id: 'ai_bookings', label: 'AI Ambassador Bookings', icon: Sparkles, path: '/admin/ai-bookings', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'ai-bookings-nav' },
  { id: 'custom_requests', label: 'Custom Ambassador Requests', icon: Palette, path: '/admin/custom-requests', iconColor: 'text-emerald-700', iconBg: 'bg-emerald-100', btnStyle: 'border-emerald-200 hover:bg-emerald-50', testId: 'custom-requests-nav' },
  { id: 'ai_analytics', label: 'AI Ambassador Analytics', icon: BarChart3, path: '/admin/ai-analytics', iconColor: 'text-indigo-700', iconBg: 'bg-indigo-100', btnStyle: 'border-indigo-200 hover:bg-indigo-50', testId: 'ai-analytics-nav' },
  { id: 'community_mod', label: 'Community Feed Moderation', icon: MessageCircle, path: '/admin/community-moderation', iconColor: 'text-orange-700', iconBg: 'bg-orange-100', btnStyle: 'border-orange-200 hover:bg-orange-50', testId: 'community-moderation-nav' },
  { id: 'admin_management', label: 'Admin Management', icon: Users, path: '/admin/admin-management', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'admin-management-nav', requires: 'can_manage_admins' },
  { id: 'activity_log', label: 'Activity Log', icon: BarChart3, path: '/admin/activity-log', iconColor: 'text-gray-700', iconBg: 'bg-gray-100', btnStyle: 'border-gray-200 hover:bg-gray-50', testId: 'activity-log-nav' },
  { id: 'shifts', label: 'Manage Shifts', icon: Briefcase, path: '/admin/shifts', iconColor: 'text-teal-600', iconBg: 'bg-teal-100', btnStyle: 'border-teal-200 hover:bg-teal-50', testId: 'admin-shifts-nav' },
  { id: 'admin_permissions', label: 'Admin Permissions', icon: Shield, path: '/admin/permissions', iconColor: 'text-cyan-700', iconBg: 'bg-cyan-100', btnStyle: 'border-cyan-200 hover:bg-cyan-50', testId: 'admin-permissions-nav', requires: 'can_manage_admins' },
  { id: 'billing_approvals', label: 'Billing Approvals', icon: DollarSign, path: '/admin/billing-approvals', iconColor: 'text-amber-600', iconBg: 'bg-amber-100', btnStyle: 'border-amber-200 hover:bg-amber-50', testId: 'billing-approvals-nav' },
  { id: 'welcome_videos', label: 'Welcome Videos', icon: Video, path: '/admin/video-settings', iconColor: 'text-violet-600', iconBg: 'bg-violet-100', btnStyle: 'border-violet-200 hover:bg-violet-50', testId: 'welcome-videos-nav' },
  { id: 'alert_prefs', label: 'Alert Settings', icon: Bell, path: '/admin/alert-preferences', iconColor: 'text-red-700', iconBg: 'bg-red-100', btnStyle: 'border-red-200 hover:bg-red-50', testId: 'alert-prefs-nav', requires: 'can_edit_billing' },
  { id: 'cleanup', label: 'Test Data Cleanup', icon: Trash2, path: '/admin/cleanup', iconColor: 'text-rose-700', iconBg: 'bg-rose-100', btnStyle: 'border-rose-200 hover:bg-rose-50', testId: 'cleanup-nav', requires: 'can_manage_admins' },
];

const ACTIONS_MAP = { talent: TALENT_ACTIONS, client: CLIENT_ACTIONS, admin: ADMIN_ACTIONS };
const STORAGE_KEY = { talent: 'ievntur_talent_qa_order', client: 'ievntur_client_qa_order', admin: 'ievntur_admin_qa_order' };

// Role-based card theming — solid backgrounds, not transparent
const CARD_THEME = {
  talent: {
    cardBg: 'bg-[#FFF1F5]',        // light pink
    border: 'border-rose-200',
    headerColor: 'text-rose-600',
    editBg: 'bg-rose-50',           // edit mode tint
    editBorder: 'border-rose-300',
  },
  client: {
    cardBg: 'bg-[#F5F3FF]',        // light purple
    border: 'border-violet-200',
    headerColor: 'text-violet-600',
    editBg: 'bg-violet-50',
    editBorder: 'border-violet-300',
  },
  admin: {
    cardBg: 'bg-[#F1F5F9]',        // slate-100
    border: 'border-slate-200',
    headerColor: 'text-slate-700',
    editBg: 'bg-slate-50',
    editBorder: 'border-slate-300',
  },
};

const QuickActionBar = ({ role = 'client', permissions = null }) => {
  const navigate = useNavigate();
  const rawActions = ACTIONS_MAP[role] || TALENT_ACTIONS;
  // Iter-435 — gate the client "Discover Talent" entry behind an active
  // paid subscription. The backend already returns 403 for free clients;
  // hiding the nav avoids a dead-end click. Admins/talent are unaffected.
  const [discoverLocked, setDiscoverLocked] = useState(false);
  useEffect(() => {
    if (role !== 'client') return undefined;
    let cancelled = false;
    // Use the same plan authority the backend Discovery 403 relies on
    // (get_active_plan_id → 'free'), so trialing/paid clients that CAN
    // access Discovery also see the nav entry.
    api.get('/monetization/my-entitlements')
      .then((res) => { if (!cancelled) setDiscoverLocked((res?.data?.plan_id || 'free') === 'free'); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [role]);
  // Permission filter (admin only) + client discovery entitlement filter
  const allActions = rawActions.filter((a) => {
    if (role === 'client' && a.id === 'discover' && discoverLocked) return false;
    if (!a.requires) return true;
    if (!permissions) return true;
    if (a.requires === 'can_manage_talent_or_admins') {
      return !!(permissions.can_manage_talent || permissions.can_manage_admins);
    }
    return !!permissions[a.requires];
  });
  const storageKey = STORAGE_KEY[role] || STORAGE_KEY.talent;
  const theme = CARD_THEME[role] || CARD_THEME.talent;

  const [orderedIds, setOrderedIds] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragItem = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const { order, hidden } = JSON.parse(saved);
        const validIds = allActions.map(a => a.id);
        const validOrder = (order || []).filter(id => validIds.includes(id));
        const missing = validIds.filter(id => !validOrder.includes(id));
        let merged = [...validOrder, ...missing];
        // Settlements is a correction to the layout — when it's newly
        // introduced to an existing user, group it directly above Instant
        // Payout instead of appending it at the very bottom.
        if (missing.includes('settlements') && merged.includes('instant_payout')) {
          merged = merged.filter(id => id !== 'settlements');
          const ip = merged.indexOf('instant_payout');
          merged.splice(ip, 0, 'settlements');
        }
        // Talent clarity: always pin Chat with Evie → Settlements → My Bookings
        // to the very top, in that order, regardless of any saved custom order.
        if (role === 'talent') {
          const pin = ['support', 'settlements', 'bookings'].filter(id => validIds.includes(id));
          merged = [...pin, ...merged.filter(id => !pin.includes(id))];
        }
        setOrderedIds(merged);
        // Never leave the pinned essentials hidden (visibility requirement).
        const neverHidden = role === 'talent'
          ? ['support', 'settlements', 'bookings']
          : ['settlements'];
        setHiddenIds((hidden || []).filter(id => validIds.includes(id) && !neverHidden.includes(id)));
        return;
      }
    } catch {}
    setOrderedIds(allActions.map(a => a.id));
    setHiddenIds([]);
  }, [role]);

  const persist = useCallback((order, hidden, showToast = true) => {
    localStorage.setItem(storageKey, JSON.stringify({ order, hidden }));
    if (showToast) {
      toast.success('Layout saved', { duration: 1500 });
      trackQuickAction('reorder', role, 'reorder');
    }
  }, [storageKey, role]);

  const handleDragStart = (e, id) => {
    dragItem.current = id;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (dragItem.current === id) return;
    setDragOverId(id);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragItem.current;
    if (!sourceId || sourceId === targetId) return;
    const newOrder = [...orderedIds];
    const srcIdx = newOrder.indexOf(sourceId);
    const tgtIdx = newOrder.indexOf(targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;
    newOrder.splice(srcIdx, 1);
    newOrder.splice(tgtIdx, 0, sourceId);
    setOrderedIds(newOrder);
    persist(newOrder, hiddenIds);
    setDraggingId(null);
    setDragOverId(null);
    dragItem.current = null;
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
    dragItem.current = null;
  };

  const moveItem = (id, direction) => {
    const newOrder = [...orderedIds];
    const idx = newOrder.indexOf(id);
    if (idx === -1) return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= newOrder.length) return;
    [newOrder[idx], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[idx]];
    setOrderedIds(newOrder);
    persist(newOrder, hiddenIds);
  };

  const toggleHidden = (id) => {
    const visibleCount = orderedIds.filter(i => !hiddenIds.includes(i)).length;
    const isHiding = !hiddenIds.includes(id);
    if (isHiding && visibleCount <= 2) {
      toast.error('Keep at least 2 actions visible');
      return;
    }
    const newHidden = isHiding ? [...hiddenIds, id] : hiddenIds.filter(h => h !== id);
    setHiddenIds(newHidden);
    persist(orderedIds, newHidden);
  };

  const actionsById = Object.fromEntries(allActions.map(a => [a.id, a]));
  const visibleActions = orderedIds.filter(id => !hiddenIds.includes(id)).map(id => actionsById[id]).filter(Boolean);

  return (
    <Card
      className={`shadow-sm overflow-hidden ${isCustomizing ? `${theme.editBg} ${theme.editBorder}` : `${theme.cardBg} ${theme.border}`}`}
      data-testid="quick-action-bar"
    >
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className={`text-sm font-semibold ${theme.headerColor}`}>
          Quick Actions
        </CardTitle>
        <button
          onClick={() => setIsCustomizing(!isCustomizing)}
          className={`transition-all p-1.5 rounded-md ${
            isCustomizing
              ? 'bg-white text-cyan-600 shadow-sm ring-1 ring-cyan-200'
              : 'text-gray-400 hover:text-gray-600 hover:bg-white/60'
          }`}
          data-testid="quick-action-customize-btn"
          title={isCustomizing ? 'Done' : 'Customize'}
        >
          <Settings className="w-4 h-4" />
        </button>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-1.5">
        {isCustomizing ? (
          <>
            {/* Instruction text */}
            <p className="text-[11px] text-gray-500 mb-2 font-medium" data-testid="edit-mode-hint">
              Drag to reorder. Tap eye to hide.
            </p>

            {orderedIds.map((id, idx) => {
              const action = actionsById[id];
              if (!action) return null;
              const Icon = action.icon;
              const isHidden = hiddenIds.includes(id);
              const isFirst = idx === 0;
              const isLast = idx === orderedIds.length - 1;

              return (
                <div
                  key={id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, id)}
                  onDragOver={(e) => handleDragOver(e, id)}
                  onDrop={(e) => handleDrop(e, id)}
                  onDragEnd={handleDragEnd}
                  className={`
                    flex items-center gap-2 w-full px-2.5 py-2 rounded-lg border text-sm transition-all
                    ${draggingId === id ? 'opacity-40 scale-[0.97]' : ''}
                    ${dragOverId === id && draggingId !== id ? 'border-cyan-400 bg-cyan-50/80' : ''}
                    ${isHidden
                      ? 'opacity-40 bg-gray-100 border-gray-200'
                      : 'bg-white border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                    }
                    cursor-grab active:cursor-grabbing
                  `}
                  data-testid={`quick-action-customize-${id}`}
                >
                  {/* Drag handle — ALWAYS visible in edit mode, clear dark color */}
                  <GripVertical className="w-4 h-4 text-gray-500 flex-shrink-0" data-testid={`drag-handle-${id}`} />

                  {/* Icon pill */}
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${action.iconBg}`}>
                    <Icon className={`w-3.5 h-3.5 ${action.iconColor}`} />
                  </div>

                  <span className="flex-1 text-left truncate text-xs font-medium text-gray-700">{action.label}</span>

                  {/* Mobile up/down arrows */}
                  <div className="flex flex-col sm:hidden flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); moveItem(id, -1); }}
                      disabled={isFirst}
                      className={`p-0.5 ${isFirst ? 'text-gray-200' : 'text-gray-500 active:text-gray-800'}`}
                      data-testid={`quick-action-move-up-${id}`}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveItem(id, 1); }}
                      disabled={isLast}
                      className={`p-0.5 ${isLast ? 'text-gray-200' : 'text-gray-500 active:text-gray-800'}`}
                      data-testid={`quick-action-move-down-${id}`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Visibility toggle */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleHidden(id); }}
                    className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${
                      isHidden
                        ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                        : 'text-cyan-500 hover:text-cyan-700 hover:bg-cyan-100'
                    }`}
                    title={isHidden ? 'Show' : 'Hide'}
                    data-testid={`quick-action-toggle-${id}`}
                  >
                    {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              );
            })}
          </>
        ) : (
          visibleActions.map(action => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={() => { trackQuickAction(action.id, role); navigate(action.path); }}
                className={`
                  w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border bg-white
                  shadow-[0_1px_3px_rgba(0,0,0,0.06)] transition-all group
                  ${action.btnStyle}
                  active:scale-[0.98] active:shadow-none
                `}
                data-testid={action.testId}
              >
                {/* Icon wrapped in colored pill */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${action.iconBg}`}>
                  <Icon className={`w-4 h-4 ${action.iconColor}`} />
                </div>
                <span className="flex-1 text-left text-sm font-medium text-gray-800">{action.label}</span>
                <GripVertical className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

export default QuickActionBar;
